/*
 * get_app_status — the Phase 2 proof tool.
 *
 * Deliberately trivial and side-effect free. Its only job is to prove the whole
 * chain works: Adobe App Builder -> browser application -> WebMCP -> AI client
 * -> structured tool result. Do not grow it into a kitchen sink.
 */

import { describeEnvironment } from '../environment.js'

export const APP_NAME = 'Mini Paint WebMCP'

// Parcel inlines process.env at build time; keep this to non-secret values only.
export const APP_VERSION = (typeof process !== 'undefined' && process.env && process.env.APP_VERSION) || '0.1.0'

export function createAppStatusTool ({ adapter, version = APP_VERSION } = {}) {
  return {
    name: 'get_app_status',
    title: 'Get application status',
    description:
      'Returns the status of this web application: whether it is running, which environment it ' +
      'is deployed in, and whether WebMCP tools are available. Read-only with no side effects — ' +
      'use it to confirm that you are connected to the page and that tool calls work.',
    readOnly: true,
    inputSchema: {
      type: 'object',
      properties: {},
      additionalProperties: false
    },
    outputSchema: {
      type: 'object',
      properties: {
        status: { type: 'string', enum: ['ok'] },
        application: { type: 'string', description: 'Human readable application name' },
        version: { type: 'string' },
        environment: { type: 'string', description: 'Where the application is hosted' },
        workspace: { type: 'string', description: 'App Builder workspace, when hosted there' },
        webMcpAvailable: { type: 'boolean' },
        webMcpTransport: { type: 'string', enum: ['native', 'polyfill', 'unavailable'] },
        registeredToolCount: { type: 'integer', minimum: 0 },
        timestamp: { type: 'string', description: 'ISO-8601 UTC timestamp of this response' }
      },
      required: ['status', 'application', 'environment', 'webMcpAvailable', 'timestamp']
    },
    handler: (_args, ctx) => {
      const env = describeEnvironment()
      return {
        status: 'ok',
        application: APP_NAME,
        version,
        environment: env.environment,
        workspace: env.workspace || undefined,
        webMcpAvailable: adapter ? adapter.available : false,
        webMcpTransport: adapter ? adapter.transport : 'unavailable',
        registeredToolCount: ctx && ctx.registry ? ctx.registry.list().length : undefined,
        timestamp: new Date().toISOString()
      }
    }
  }
}
